import { GROUP_TYPES } from "../../../Enums/GroupEnum";
import { IEvent } from "../../../interfaces/models/IEvent";
import IFirm from "../../../interfaces/models/IFirm";
import { IGroup } from "../../../interfaces/models/IGroup";
import { IGroupMembership } from "../../../interfaces/models/IGroupMembership";

export interface PreferenceEventsOptions {
  events: IEvent[];
  groups?: IGroup[];
  memberships?: IGroupMembership[];
  personFirmId?: string;
  personFirm?: Pick<IFirm, "region"> | null;
  maxEvents: number;
}

type ComparableGroupBuckets = {
  all: Set<string>;
  practice: Set<string>;
  industry: Set<string>;
  specialty: Set<string>;
};

const normalizeComparable = (value?: string | null): string =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const addComparable = (target: Set<string>, value?: string | null): void => {
  const normalized = normalizeComparable(value);
  if (normalized) {
    target.add(normalized);
  }
};

const getTruthyBoolean = (value: unknown): boolean =>
  value === true || value === 1;

const buildGroupById = (groups: IGroup[]): Map<string, IGroup> =>
  new Map(
    groups
      .filter((group) => Boolean(group.id))
      .map((group) => [normalizeComparable(group.id), group]),
  );

const getGroupByAnyKey = (
  value: string,
  groupById: Map<string, IGroup>,
): IGroup | undefined => groupById.get(normalizeComparable(value));

const getEffectiveGroup = (
  group: IGroup | undefined,
  groupById: Map<string, IGroup>,
): IGroup | undefined => {
  if (!group?.globalGroupId) {
    return group;
  }

  return (
    getGroupByAnyKey(group.globalGroupId, groupById) ||
    group
  );
};

const collectComparableGroupValues = (
  value: string,
  groupById: Map<string, IGroup>,
): Set<string> => {
  const comparableValues = new Set<string>();
  addComparable(comparableValues, value);

  const rawGroup = getGroupByAnyKey(value, groupById);
  const effectiveGroup = getEffectiveGroup(rawGroup, groupById);

  [rawGroup, effectiveGroup].forEach((group) => {
    addComparable(comparableValues, group?.id);
    addComparable(comparableValues, group?.title);
  });

  return comparableValues;
};

const hasSetIntersection = (left: Set<string>, right: Set<string>): boolean => {
  for (const value of left) {
    if (right.has(value)) {
      return true;
    }
  }

  return false;
};

const getComparableRegionIds = (regions: unknown): Set<string> => {
  const comparableValues = new Set<string>();

  if (!Array.isArray(regions)) {
    return comparableValues;
  }

  regions.forEach((region) => {
    if (typeof region === "string") {
      addComparable(comparableValues, region);
      return;
    }

    if (
      region &&
      typeof region === "object" &&
      "id" in region &&
      typeof (region as { id?: unknown }).id === "string"
    ) {
      addComparable(comparableValues, (region as { id: string }).id);
    }
  });

  return comparableValues;
};

const buildComparableMemberGroups = (
  memberships: IGroupMembership[],
  groups: IGroup[],
): ComparableGroupBuckets => {
  const comparableGroups: ComparableGroupBuckets = {
    all: new Set<string>(),
    practice: new Set<string>(),
    industry: new Set<string>(),
    specialty: new Set<string>(),
  };

  const groupById = buildGroupById(groups);

  memberships
    .filter(
      (membership) =>
        Boolean(membership.groupId) &&
        getTruthyBoolean(membership.isMember) &&
        membership.hidden !== true,
    )
    .forEach((membership) => {
      const rawGroup = getGroupByAnyKey(membership.groupId, groupById);
      const effectiveGroup = getEffectiveGroup(rawGroup, groupById);
      const comparableValues = collectComparableGroupValues(
        membership.groupId,
        groupById,
      );

      comparableValues.forEach((value) => comparableGroups.all.add(value));

      const groupType = effectiveGroup?.type || rawGroup?.type;
      if (groupType === GROUP_TYPES.PRACTICE) {
        comparableValues.forEach((value) => comparableGroups.practice.add(value));
      } else if (groupType === GROUP_TYPES.INDUSTRY) {
        comparableValues.forEach((value) => comparableGroups.industry.add(value));
      } else if (
        groupType === GROUP_TYPES.ASSOCIATION ||
        groupType === GROUP_TYPES.WSGOPEN
      ) {
        comparableValues.forEach((value) =>
          comparableGroups.specialty.add(value),
        );
      }
    });

  return comparableGroups;
};

const getEventComparableGroupEntries = (
  values: string[] | undefined,
  groupById: Map<string, IGroup>,
): Set<string>[] =>
  (values || [])
    .filter(Boolean)
    .map((value) => collectComparableGroupValues(value, groupById));

const isGroupMeetingEvent = (event: IEvent): boolean => {
  const normalizedSetupType = normalizeComparable(event.setupType);
  return (
    normalizedSetupType === "group" ||
    normalizedSetupType === "group meeting" ||
    normalizedSetupType === "groupwebinar" ||
    normalizedSetupType === "group meeting webinar"
  );
};

const getMatchedRules = (
  event: IEvent,
  options: Omit<PreferenceEventsOptions, "events" | "maxEvents">,
  comparableGroups: ComparableGroupBuckets,
  groupById: Map<string, IGroup>,
): number[] => {
  const matchedRules: number[] = [];
  const personFirmId = normalizeComparable(options.personFirmId);

  if (
    personFirmId &&
    (event.sponsorHost || []).some(
      (sponsor) =>
        sponsor.type === "member" &&
        normalizeComparable(sponsor.id) === personFirmId,
    )
  ) {
    matchedRules.push(1);
  }

  const comparableEventSponsorGroups = (event.sponsorHost || [])
    .filter((sponsor) => sponsor.type === "wsg_group" && sponsor.id)
    .map((sponsor) => collectComparableGroupValues(sponsor.id, groupById));

  if (
    isGroupMeetingEvent(event) &&
    comparableEventSponsorGroups.some((entry) =>
      hasSetIntersection(entry, comparableGroups.all),
    )
  ) {
    matchedRules.push(2);
  }

  const eventRegionIds = getComparableRegionIds(event.regionIds || []);
  const firmRegionIds = getComparableRegionIds(options.personFirm?.region || []);
  if (eventRegionIds.size && firmRegionIds.size && hasSetIntersection(eventRegionIds, firmRegionIds)) {
    matchedRules.push(3);
  }

  const comparableEventGroups = getEventComparableGroupEntries(
    event.groupIds,
    groupById,
  );

  if (
    comparableEventGroups.some((entry) =>
      hasSetIntersection(entry, comparableGroups.practice),
    )
  ) {
    matchedRules.push(4);
  }

  if (
    comparableEventGroups.some((entry) =>
      hasSetIntersection(entry, comparableGroups.industry),
    )
  ) {
    matchedRules.push(5);
  }

  if (
    comparableEventGroups.some((entry) =>
      hasSetIntersection(entry, comparableGroups.specialty),
    )
  ) {
    matchedRules.push(6);
  }

  return matchedRules;
};

const sortByMostRecent = (left: IEvent, right: IEvent): number => {
  const leftCreatedAt = left.createdDate || left._ts || 0;
  const rightCreatedAt = right.createdDate || right._ts || 0;

  if (leftCreatedAt !== rightCreatedAt) {
    return rightCreatedAt - leftCreatedAt;
  }

  return (left.startDate || 0) - (right.startDate || 0);
};

export const getPreferenceEvents = ({
  events,
  groups = [],
  memberships = [],
  personFirmId,
  personFirm,
  maxEvents,
}: PreferenceEventsOptions): IEvent[] => {
  if (!events.length || maxEvents <= 0) {
    return [];
  }

  const groupById = buildGroupById(groups);
  const comparableGroups = buildComparableMemberGroups(memberships, groups);

  return events
    .map((event) => ({
      event,
      matchedRules: getMatchedRules(
        event,
        {
          groups,
          memberships,
          personFirmId,
          personFirm,
        },
        comparableGroups,
        groupById,
      ),
    }))
    .filter((entry) => entry.matchedRules.length > 0)
    .sort((left, right) => {
      const leftPriority = Math.min(...left.matchedRules);
      const rightPriority = Math.min(...right.matchedRules);

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      if (left.matchedRules.length !== right.matchedRules.length) {
        return right.matchedRules.length - left.matchedRules.length;
      }

      return sortByMostRecent(left.event, right.event);
    })
    .slice(0, maxEvents)
    .map((entry) => entry.event);
};
